import { getActualEndpoint, getApiVersion } from './apiVersions.ts';
import { getUrlQueryStringPart } from './utils';

/** Empty fallback keeps the app booting so the diagnostics in utils/diagnostics report the cause. */
const runtimeConfig = window.anovaAppConfig ?? {};

export const {
  API_URL,
  DMS_API_URL,
  API_URL_FOR_LISTS,
  UPLOAD_FILES_URL,
  UPLOAD_IMAGES_URL,
  BASE_PDF_URL,
  PDF_GENERATOR_ACCESS_CODE,
  SENTRY_DSN,
  BUILD_NUMBER,
  SENTRY_ENV,
} = runtimeConfig;

export const TIMEOUT_AUTO_LOGOUT = parseInt(runtimeConfig.TIMEOUT_AUTO_LOGOUT);
export const ENGINEERING_WORK = parseInt(runtimeConfig.ENGINEERING_WORK);

const providedSentryTracesSampleRate = parseFloat(
  runtimeConfig.SENTRY_TRACES_SAMPLE_RATE,
);
const providedSentryErrorReplaysRate = parseFloat(
  runtimeConfig.SENTRY_REPLAYS_ON_ERROR_RATE,
);

export const SENTRY_TRACES_SAMPLE_RATE = !isNaN(providedSentryTracesSampleRate)
  ? providedSentryTracesSampleRate
  : 0;
export const SENTRY_REPLAYS_ON_ERROR_RATE = !isNaN(
  providedSentryErrorReplaysRate,
)
  ? providedSentryErrorReplaysRate
  : 0;

export const REDUX_LOGGER_IS_ENABLED = false;
export const LOCAL_STORAGE_PREFIX = '@ANOVA';
export const NODE_ENV = process.env.NODE_ENV;
export const ANOVA_WEBSITE_URL = '//www.anovaevidence.com';
export const SUPPORT_EMAIL = 'support@anovaos.com';

export const DISABLE_PASSWORD_VALIDATION =
  parseInt(runtimeConfig.DISABLE_PASSWORD_VALIDATION) === 1;

export const ENABLE_DMS = parseInt(runtimeConfig.ENABLE_DMS) === 1;
/** Personnel Document Access management UI. Off unless UNSAFE_ENABLE_DOCUMENT_ACCESS=1. */
export const ENABLE_DOCUMENT_ACCESS =
  parseInt(runtimeConfig.ENABLE_DOCUMENT_ACCESS) === 1;

export const ROUTES = {
  FORGOT_PASSWORD: '/forgot-password',
  HOME_PAGE: '/',
  NOT_FOUND: '/404',
  ADMIN_HOME: '/admin',
  ADMIN_PATIENTS: '/admin/patients',
  USER_PROFILE: '/profile',
  USER_PROFILE_EDIT: '/profile-edit',
  TEST_PAGE: '/test',
  TEST_FORM_PAGE: '/test-form',
  LOGIN: '/login',
  RESET_PASSWORD: '/reset-password',
  SIGN_UP: '/sign-up',
  SIGN_UP_INVITE: '/sign-up-invite/',
  SIGN_UP_INVITE_FROM: (token) => `/inactive/invited-from?token=${token}`,
  CONNECTION_ERROR: '/connection-error',
  BASIC_ACCOUNT_SETUP: '/basic-account-setup',
  REPRESENTATIVE_REGISTRATION: '/representative-registration',
  RESEARCH_SITE_FORM: '/research-site-form',
  ADD_STUDY_MANUALLY_FORM: '/add-study-manually-form',
  EDIT_STUDY_FORM: '/edit-study-form',
  SPONSOR_STUDY_FORM: '/sponsor-study-form',
  PERSONNEL_SPONSOR_STUDY_FORM: '/personnel-sponsor-study-form',
  DELETED_USER: '/deleted',
  NOT_AVAILABLE: '/not-available',
  VERSION: '/version',
  // PERSONNEL FORM REGISTRATION
  PERSONNEL_FORM: '/personnel-form',
  ADD_PERSONNEL_FORM: '/add-personnel-form',
  // CONGRATULATIONS
  CONGRATULATION: '/congratulation',
  CONGRATULATION_SIGN_UP: '/congratulation/sign-up',
  CONGRATULATION_REPRESENTATIVE_REGISTRATION:
    '/congratulation/representative-registration',
  CONGRATULATION_LINK_STUDY: '/congratulation/link-study',
  CONGRATULATION_ADMIN_VERIFICATION: '/congratulation/admin-verification',
  WELCOME_AFTER_LOGIN: '/congratulation/log-in',
  CONGRATULATION_ADDED_STUDIES: '/congratulation/study',
  CONGRATULATION_ADDED_PROFILE_STUDIES: '/congratulation/profile-study',
  CONGRATULATION_LINK_PERSONNEL: '/congratulation/link-personnel',
  //DECLINED
  VERIFICATION_DECLINE: '/verification-declined',
  // FULL REGISTERED USERS
  CHANGE_PASSWORD: '/change-password',
  CHANGE_EMAIL: '/change-email',
  MY_SPONSORS: '/my-sponsors',
  MY_RESEARCH_SITES: '/my-research-sites',
  SPONSOR_PROFILE_REQUESTS: '/sponsor-prof-req',
  INACTIVE_SPONSORS: '/inactive-sponsors',
  INACTIVE_RESEARCH_SITE: '/inactive-research-site',
  RESEARCH_SITE_PROFILE_REQUESTS: '/site-prof-req',
  RESEARCH_SITES: '/research-sites',
  SETTINGS: '/settings',
  ORGANIZATION_SETTINGS: '/organization-settings',
  SPONSORS: '/sponsors',
  REPRESENTATIVE: '/representative',
  ORGANIZATION_INFO: '/organization-info',
  EDIT_STUDY: '/edit-study',
  ADD_STUDY: '/add-study',
  EDIT_LOCATION: '/edit-location',
  ADD_LOCATION: '/add-location',
  ACTIVITY: '/activity',
  ALL_STUDIES: '/studies',
  APPLICATION: '/application',
  APPLICATIONS_BY_SPONSOR: '/applications-sponsor',
  APPLICATIONS_BY_RESEARCH_SITE: '/applications-research-site',
  ADD_APPLICATION: '/add-application',
  PROFILE_STUDY: '/profile-study',
  PROFILE_LOCATIONS: '/profile-locations',
  ADD_PROFILE_STUDY: '/add-profile-study',
  LINK_STUDIES_FOR_RESEARCH_SITE: '/link-studies-for-research-site',
  PROFILE_LOCATION: '/profile-location',
  EDIT_APPLICATION: '/edit-application',
  PROFILE_PERSONNEL: '/profile-personnel',
  ALL_PERSONNEL: '/personnel',
  LINKING_REQUESTS: '/linking-requests',
  PERSONNEL_RESEARCH_SITE: '/personnel-research-site',
  // PATIENTS
  ALL_PATIENTS: '/patients',
  PATIENTS_ON_STUDIES: '/patients-on-studies',
  PATIENT_ON_STUDIES: '/patient-on-studies',
  // PATIENT
  PATIENT: '/patient',
  PATIENT_NEW_MATCHES_FIND: '/matches-find',
  PATIENT_NEW_MATCHES_FOUND: '/matches-found',
  PATIENT_NEW_DEMOGRAPHICS: '/demographics',
  PATIENT_NEW: '/new-patient',
  CHANGE_EMAIL_CONFIRMATION: '/change-email-confirmation',
  DMS_DOCUMENTS: '/documents',
  DMS_MILESTONES: '/milestones',
};

const buildVersionedUrl = (
  requestedEndpoint,
  requestedVersion,
  additionalParams = {},
) => {
  const endpoint = getActualEndpoint(requestedEndpoint, requestedVersion);
  const actualVersion = getApiVersion(endpoint, requestedVersion);

  const params = {
    'api-version': actualVersion,
    ...additionalParams,
  };

  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${endpoint}?${queryString}`;
};

export const API_METHODS = {
  //ACCESS REQUEST
  ACCESS_REQUEST_SPONSOR: '/access/request/sponsor',
  ACCESS_REQUEST_RESEARCH_SITE: '/access/request/research-site',
  ACCESS_INVITE_ACCEPT: (requestId) => `/access/invite/accept/${requestId}`,
  ACCESS_INVITE_ACCEPT_PROFILE: (userId) =>
    `/access/invite/accept/user/${userId}`,
  ACCESS_INVITE_DENY: (requestId) => `/access/invite/deny/${requestId}`,
  ACCESS_INVITE_DENY_PROFILE: (userId) => `/access/invite/deny/user/${userId}`,
  ACCESS_REQUEST_INVITE_DISCONNECT: (requestId) =>
    `/access/disconnect/request/${requestId}`,
  ACCESS_REQUEST_DISCONNECT_PROFILE: (userId) => `/access/disconnect/${userId}`,
  ACCESS_REQUEST_DISCONNECT: (requestId) =>
    `/access/request/disconnect/user/${requestId}`,
  ACCESS_REQUEST_SPONSOR_ORGANIZATIONS: '/access/request/sponsor/organizations',
  // AUTH
  AUTH_LOGIN: '/auth/token',
  AUTH_MFA_RESEND_VALIDATION_CODE: '/auth/mfa/resubmit',
  AUTH_MFA_VALIDATE_CODE: '/auth/mfa/validate',
  AUTH_REFRESH: '/auth/token',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_STUDIES_COUNT: (Id) => `/sponsors/${Id}/studies/count`,
  //USER
  USER: '/user',
  USER_FORGOT_PASSWORD: '/password/email',
  USER_RESET_PASSWORD: '/password/recovery',
  USER_SETTINGS: '/user/settings',
  USER_CHANGE_EMAIL: '/user/change-email',
  // REGISTRATION
  REG_COMMON: '/registration/common',
  REG_SHORT: '/registration/short',
  REG_ROLE: '/registration/role',
  REG_PROFILE: '/registration/profile',
  REG_ORGANIZATION: '/registration/organization',
  REG_ACADEMIC_QUALIFICATION: '/registration/academic-qualification',
  REG_RESEARCH_EXPERIENCE: '/registration/research-experience',
  REG_FINANCIAL_DISCLOSURE: '/registration/financial-disclosure',
  REG_REGULATORY_OVERSIGHT: '/registration/regulatory-oversight',
  REG_EMPLOYMENT_HISTORY: '/registration/employment-history',
  REG_SUBMIT: '/registration/submit',
  // VERIFICATION
  VERIFICATION_SUBMIT: '/verificationRequest',
  // SPONSOR REGISTRATION
  REG_SPONSOR: '/registration/sponsor',
  REG_SPONSOR_ORGANIZATION: '/registration/sponsor/organization',
  REG_SPONSOR_GENERAL: '/registration/sponsor/general',
  REG_SPONSOR_CONTACTS: '/registration/sponsor/contacts',
  REG_SPONSOR_STUDIES: '/registration/sponsor/studies',
  REG_SPONSOR_STUDIES_COUNT: '/registration/sponsor/studies/count',
  // RESEARCH SITE REGISTRATION
  REG_RES_SITE: '/registration/research-site/submit',
  REG_RES_SITE_LOCATIONS: '/registration/research-site/locations',
  REG_RES_SITE_LOCATIONS_COUNT: '/registration/research-site/locations/count',
  REG_RES_SITE_LATEST: '/registration/research-site/latest',
  REG_RES_SITE_ORG_INFO: '/registration/research-site/organization-info',
  REG_RES_SITE_LOCATION: '/registration/research-site/location',
  REG_RES_SITE_LOCATION_BY_ID: (locationId) =>
    `/registration/research-site/locations/${locationId}`,
  REG_RES_SITE_LOCATION_DELETE: (locationId) =>
    `/registration/research-site/locations/${locationId}`,
  REG_RES_SITE_SIDEBAR_INFO: (locationId) =>
    `/registration/research-site/locations/${locationId}/sidebar-information`,
  REG_RES_SITE_LOCATIONS_LIST:
    '/registration/research-site/simplified-locations-without-principal',
  REG_RES_SITE_LOCATIONS_LIST_CHANGE_PRINCIPAL: (locationId) =>
    `/registration/research-site/locations/${locationId}/principal`,
  REG_RES_SITE_STUDY: '/registration/research-site/study',
  REG_RES_SITE_CAPABILITIES: '/registration/research-site/capabilities',
  REG_RES_SITE_INVEST_PRODUCT_STORAGE:
    '/registration/research-site/investigational-product-storage',
  REG_RES_SITE_DRUG_PREPARATION: '/registration/research-site/drug-preparation',
  REG_RES_SITE_SAMPLE_STORAGE: '/registration/research-site/sample-storage',
  REG_RES_SITE_SAMPLE_PREPARATION:
    '/registration/research-site/sample-preparation',
  REG_RES_SITE_REAGENT_STORAGE: '/registration/research-site/reagent-storage',
  REG_RES_SITE_MEDICAL_WASTE: '/registration/research-site/medical-waste',
  REG_RES_SITE_AUTOPOPULATED_MEDICAL_WASTE:
    '/registration/research-site/auto-populated-medical-waste-contact',
  REG_RES_SITE_MEDICAL_WASTE_DESTRUCTION:
    '/registration/research-site/medical-waste-destruction',
  REG_RES_SITE_INFORMATION_TECHNOLOGY:
    '/registration/research-site/information-technology',
  REG_RES_SITE_MONITORING_FACILITIES:
    '/registration/research-site/monitoring-facilities',
  REG_RES_SITE_IRB_IEC: '/registration/research-site/irb-iec',
  REG_RES_SITE_IRB_INDEPENDENT: '/registration/research-site/independent-irb',
  REG_RES_SITE_LABORATORY_FACILITY:
    '/registration/research-site/laboratory-facility',
  REG_RES_SITE_EXTERNAL_LABORATORY_FACILITY:
    '/registration/research-site/external-laboratory-facility',
  REG_RES_SITE_IMAGING: '/registration/research-site/imaging',
  REG_RES_SITE_EXTERNAL_IMAGING: '/registration/research-site/external-imaging',
  REG_RES_SITE_CARDIOLOGY: '/registration/research-site/cardiology',
  REG_RES_SITE_EXTERNAL_CARDIOLOGY:
    '/registration/research-site/external-cardiology',
  REG_RES_SITE_INTERVENTIONAL_RADIOLOGY:
    '/registration/research-site/interventional-radiology',
  REG_RES_SITE_OPHTHALMOLOGY: '/registration/research-site/ophthalmology',
  REG_RES_SITE_VERIFY_PERSONNEL: (locationId) =>
    `/registration/research-site/personnel/${locationId}`,
  REG_RES_SITE_LOCATION_MANAGER: 'registration/research-site/location-manager',
  REG_RES_SITE_ADDITIONAL_PERSONNEL:
    'registration/research-site/additional-personnel',
  EDIT_RESEARCH_SITE_FORM_LOCATION_MANAGER: `/research-sites/personnel/email`,
  CAN_EDIT__RESEARCH_SITE_FORM_LOCATION_MANAGER: (personnelId) =>
    `personnels/${personnelId}/can-edit`,
  EDIT_RESEARCH_SITE_FORM_ADDITIONAL_PERSONNEL: (locationId) =>
    `/registration/research-site/additional-personnel/${locationId}`,
  //NOTIFICATIONS
  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_NEW: '/notifications/new',
  //PASSWORD
  PASSWORD_RESET: '/password/reset',
  // REPRESENTATIVE
  REPRESENTATIVE: '/representative',
  REPRESENTATIVE_ORGANIZATION: '/representative/organization',
  REPRESENTATIVE_ACADEMIC_QUALIFICATION:
    '/representative/academic-qualification',
  REPRESENTATIVE_CLINICAL_QUALIFICATION: (userId) =>
    `/representative/${userId}/clinical-qualification`,
  REPRESENTATIVE_RESEARCH_EXPERIENCE: '/representative/research-experience',
  REPRESENTATIVE_FINANCIAL_DISCLOSURE: '/representative/financial-disclosure',
  REPRESENTATIVE_REGULATORY_OVERSIGHT: '/representative/regulatory-oversight',
  REPRESENTATIVE_EMPLOYMENT_HISTORY: '/representative/employment-history',
  REPRESENTATIVE_PROFILE: '/representative/profile',
  // INTEGRATION
  INTEGRATION_STUDIES_COUNTRIES: '/integration/studies-countries',
  // VALUES
  VALUES: '/Values',
  EMAIL_CHANGE: '/user/email',
  // SPONSORS
  SPONSORS: '/sponsors',
  SPONSOR_BY_ID: (id) => `/sponsors/${id}`,
  SPONSORS_MY: '/sponsors/my',
  SPONSORS_ORGANIZATIONS: '/sponsors/organizations',
  SPONSORS_MY_ORGANIZATIONS: '/sponsors/my/organizations',
  SPONSORS_STUDIES: '/sponsors/studies',
  SPONSOR_STUDIES: '/sponsor/studies',
  SPONSOR_STUDIES_BY_SPONSOR_ID: (sponsorId) => `/sponsor/studies/${sponsorId}`,
  SPONSOR_STUDIES_MY: '/sponsor/studies/my',
  SPONSORS_STUDIES_ORGANIZATION: '/sponsors/studies/organization',
  SPONSORS_PERSONNEL_ADDITIONAL: '/sponsors/personnel/additional',
  SPONSORS_STUDIES_GENERAL: '/sponsors/studies/general',
  SPONSORS_STUDIES_CONTACTS: '/sponsors/studies/contacts',
  SPONSOR_LINKED: '/sponsors/linked',
  SPONSOR_USERID: '/sponsors/userId',
  RESEARCH_SITE: '/research-sites',
  RESEARCH_SITE_USER_ID: (organizationId) =>
    `/research-sites/userId/${organizationId}`,
  SPONSORS_STUDIES_BY_ID: (userId, studyId) =>
    `/sponsors/${userId}/studies/${studyId}`,
  SPONSOR_STUDIES_ASSOCIATE_BY_ID: (id) => `/sponsor/studies/associate/${id}`,
  // RESEARCH SITES
  INDEPENDENT_IRB_TEMPLATE_GET: (key) => `/independent-irb-template/${key}`,
  RESEARCH_SITE_LOCATIONS_BY_ID: (userId, locationId) =>
    `/research-site-locations/${userId}/locations/${locationId}`,
  RESEARCH_SITE_INDEPENDENT_IRB_BY_ID: (locationId) =>
    `research-site-locations/${locationId}/independent-irb`,
  RESEARCH_SITE_ALL_LOCATIONS_BY_ID: (organizationId) =>
    `/research-sites/${organizationId}/locations`,
  RESEARCH_SITE_ALL_LOCATIONS_BY_ID_GET: (organizationId) =>
    `/research-site-locations/${organizationId}/locations`,
  RESEARCH_SITE_LOCATIONS_LIST_BY_ID_GET: (organizationId) =>
    `/research-site-locations/${organizationId}/simplified-locations-without-principal`,
  RESEARCH_SITE_ADDITIONAL_PERSONNEL_BY_ID: (userId, locationId) =>
    `/research-site-locations/${userId}/locations/${locationId}/additional-personnels`,
  RESEARCH_SITE_ALL_LOCATIONS_SIMPLE_BY_ID: (organizationId) =>
    `/research-site-locations/${organizationId}/simplified-locations`,
  RESEARCH_SITES_ALL_STUDIES_BY_ID: (organizationId) =>
    `/research-sites/${organizationId}/studies`,
  RESEARCH_SITE_SIDEBAR_INFO: (userId) =>
    `/research-site-locations/${userId}/sidebar-information`,
  RESEARCH_SITE_ON_MAP: '/research-sites/on-map-batch',
  RESEARCH_SITE_PROBLEMS: '/research-site-locations/problems',
  RESEARCH_SITE_ON_MAP_ID: (id, locationId) =>
    `/research-sites/${id}/on-map-info/${locationId}`,
  RESEARCH_SITE_ORGANIZATIONS: '/research-sites/organizations',
  RESEARCH_SITE_MY: '/research-sites/my',
  RESEARCH_SITE_MY_ORGANIZATIONS: '/research-sites/my/organizations',
  RESEARCH_SITE_ORGANIZATION_INFO: '/research-sites/organization-info',
  RESEARCH_SITE_LOCATIONS_GET: '/research-site-locations/locations',
  RESEARCH_SITE_LOCATIONS: '/research-sites/locations',
  RESEARCH_SITE_LOCATIONS_POST: '/research-site-locations',
  RESEARCH_SITE_LOCATION_ADDRESS: '/research-sites/location',
  RESEARCH_SITE_LOCATIONS_IRB_INDEPENDENT:
    '/research-site-locations/independent-irb',
  RESEARCH_SITE_CAPABILITIES: '/research-sites/capabilities',
  RESEARCH_SITE_STUDIES_LINK: '/research-sites/studies/link',
  RESEARCH_SITE_STUDIES_UNLINK: '/research-sites/studies/unlink',
  RESEARCH_SITE_LOCATIONS_STUDY: '/research-sites/study',
  RESEARCH_SITE_LOCATIONS_MEDICAL_WASTE_CONTACT:
    '/research-sites/auto-populated-medical-waste-contact',
  RESEARCH_SITE_LOCATIONS_MANAGER: '/research-sites/location-manager',
  RESEARCH_SITE_LOCATIONS_INFORMATION_TECHNOLOGY:
    '/research-sites/information-technology',
  RESEARCH_SITE_LOCATIONS_MONITORING_FACILITIES:
    '/research-sites/monitoring-facilities',
  RESEARCH_SITE_LOCATIONS_IRB_IEC: '/research-sites/irb-iec',
  RESEARCH_SITE_LOCATIONS_LABORATORY: '/research-sites/laboratory-facility',
  RESEARCH_SITE_LOCATIONS_EXTERNAL_LABORATORY:
    '/research-sites/external-laboratory-facility',
  RESEARCH_SITE_LOCATIONS_IMAGING: '/research-sites/imaging',
  RESEARCH_SITE_LOCATIONS_EXTERNAL_IMAGING: '/research-sites/external-imaging',
  RESEARCH_SITE_LOCATIONS_CARDIOLOGY: '/research-sites/cardiology',
  RESEARCH_SITE_LOCATIONS_EXTERNAL_CARDIOLOGY:
    '/research-sites/external-cardiology',
  RESEARCH_SITE_LOCATIONS_SURGERY_CENTER:
    '/research-sites/interventional-radiology',
  RESEARCH_SITE_LOCATIONS_OPHTHALMOLOGY: '/research-sites/ophthalmology',
  RESEARCH_SITE_INVEST_PRODUCT_STORAGE:
    '/research-sites/investigational-product-storage',
  RESEARCH_SITE_DRUG_PREPARATION: '/research-sites/drug-preparation',
  RESEARCH_SITE_SAMPLE_STORAGE: '/research-sites/sample-storage',
  RESEARCH_SITE_SAMPLE_PREPARATION: '/research-sites/sample-preparation',
  RESEARCH_SITE_REAGENT_STORAGE: '/research-sites/reagent-storage',
  RESEARCH_SITE_MEDICAL_WASTE: '/research-sites/medical-waste',
  RESEARCH_SITE_MEDICAL_WASTE_DESTRUCTION:
    '/research-sites/medical-waste-destruction',
  RESEARCH_SITE_LINKED: '/research-sites/linked',
  // STUDIES
  REFERENCES: (referenceId) => `/references/${referenceId}`,
  STUDIES: '/sponsor/studies',
  STUDIES_all: '/all-studies',
  STUDIES_SPONSOR_CLINICAL_TRIALS: '/sponsor/studies/clinical-trials',
  STUDIES_SEND_CLINICALTRIALS: '/sponsor/studies',
  STUDIES_SEND_SUBMIT: '/sponsor/studies/submit',
  STUDIES_REGISTRATION_MY: '/sponsor/studies/registration/my',
  STUDIES_REGISTRATION_NEW_STUDY_WITH_APPLICATION:
    '/sponsor/studies/registration/new-study-with-application/my',
  // INACTIVE
  INACTIVE_INVITE: (token) => `/inactive/user?token=${token}`,
  INACTIVE_SPONSORS: '/inactive/sponsors',
  INACTIVE_SPONSOR_INVITE: (id) => `/inactive/sponsor/${id}/invite`,
  INACTIVE_RESEARCH_SITE: '/inactive/research-sites',
  INACTIVE_RESEARCH_INVITE: (id) => `/inactive/research-site/${id}/invite`,
  // ADD MANUALLY STUDIES
  STUDIES_SPONSOR: '/sponsor/studies/sponsor',
  STUDIES_SPONSOR_BY_ID: (studyId) => `/sponsor/studies/${studyId}/sponsor`,
  STUDIES_COLLABORATOR: '/sponsor/studies/collaborator',
  STUDIES_COLLABORATOR_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/collaborator`,
  STUDIES_GENERAL_INFORMATION: '/sponsor/studies/general-study-information',
  STUDIES_GENERAL_INFORMATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/general-study-information`,
  STUDIES_MULTICENTER: '/sponsor/studies/multicenter',
  STUDIES_MULTICENTER_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/multicenter`,
  STUDIES_INDICATION: '/sponsor/studies/indication',
  STUDIES_INDICATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/indication`,
  STUDIES_RESEARCH_SITE_LOCATION: '/sponsor/studies/research-site-location',
  STUDIES_RESEARCH_SITE_LOCATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/research-site-location`,
  STUDIES_DIRECT_ACCESS: '/sponsor/studies/direct-access',
  STUDIES_DIRECT_ACCESS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/direct-access-to-source-data-or-documents`,
  STUDIES_QUALITY_CONTROL_AND_ASSURANCE: '/sponsor/studies/quality-control',
  STUDIES_QUALITY_CONTROL_AND_ASSURANCE_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/quality-control-and-quality-assurance`,
  STUDIES_ETHICS: '/sponsor/studies/ethics',
  STUDIES_ETHICS_BY_ID: (studyId) => `/sponsor/studies/${studyId}/ethics`,
  STUDIES_TARGET_PATIENT_POPULATION:
    '/sponsor/studies/target-patient-population',
  STUDIES_TARGET_PATIENT_POPULATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/target-patient-population`,
  STUDIES_STUDY_ARMS: '/sponsor/studies/study-arms',
  STUDIES_STUDY_ARMS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/study-arms`,
  STUDIES_TRIAL_PURPOSE_SUMMARY: '/sponsor/studies/trial-purpose-summary',
  STUDIES_TRIAL_PURPOSE_SUMMARY_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/trial-purpose-summary`,
  STUDIES_OBJECTIVES: '/sponsor/studies/objectives',
  STUDIES_OBJECTIVES_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/objectives`,
  STUDIES_PRIMARY_ENDPOINTS: '/sponsor/studies/primary-endpoints',
  STUDIES_PRIMARY_ENDPOINTS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/primary-endpoint`,
  STUDIES_STUDY_DESIGN: '/sponsor/studies/study-design',
  STUDIES_STUDY_DESIGN_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/study-design`,
  STUDIES_INTERVENTIONS: '/sponsor/studies/interventions',
  STUDIES_INTERVENTIONS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/interventions`,
  STUDIES_CRITERIA: '/sponsor/studies/eligibility-criteria',
  STUDIES_CRITERIA_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/eligibility`,
  STUDIES_STATISTICAL_METHOD_DESCRIPTION:
    '/sponsor/studies/statistical-method-description',
  STUDIES_STATISTICAL_METHOD_DESCRIPTION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/statistical-method-description`,
  STUDIES_STATEMENT_PROPOSED_STATISTICAL:
    '/sponsor/studies/statement-proposed-statistical',
  STUDIES_STATEMENT_PROPOSED_STATISTICAL_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/statement-proposed-statistical`,
  STUDIES_SCHEDULE_ACTIVITIES: '/sponsor/studies/schedule-of-activities-list',
  STUDIES_SCHEDULE_ACTIVITIES_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/schedule-of-activities-list`,
  STUDIES_DESCRIPTION_INTERIM_ANALYSIS:
    '/sponsor/studies/description-interim-analysis',
  STUDIES_DESCRIPTION_INTERIM_ANALYSIS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/description-of-interim-analyses`,
  STUDIES_DATA_HANDLING: '/sponsor/studies/data-handling-and-record-keeping',
  STUDIES_DATA_HANDLING_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/data-handling-and-record-keeping`,
  STUDIES_FINANCING_INSURANCE: '/sponsor/studies/financing-and-insurance',
  STUDIES_FINANCING_INSURANCE_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/financing-and-insurance`,
  STUDIES_PUBLICATION_POLICY: '/sponsor/studies/publication-policy',
  STUDIES_PUBLICATION_POLICY_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/publication-policy`,
  STUDIES_SUMMARY_CLINICAL:
    '/sponsor/studies/summary-clinical-and-non-clinical-findings',
  STUDIES_SUMMARY_CLINICAL_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/summary-clinical-and-non-clinical-findings`,
  STUDIES_SUMMARY_KNOWS:
    '/sponsor/studies/summary-known-and-potential-risks-and-benefits',
  STUDIES_SUMMARY_KNOWS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/summary-of-known-and-potential-risks-and-benefits`,
  STUDIES_REFERENCE_CITATIONS: '/sponsor/studies/reference-citations',
  STUDIES_REFERENCE_CITATIONS_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/reference-citations`,
  STUDIES_STUDY_POPULATION: '/sponsor/studies/study-population',
  STUDIES_STUDY_POPULATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/study-population`,
  STUDIES_TRIAL_OBJECTIVES_PURPOSE:
    '/sponsor/studies/trial-objectives-and-purpose',
  STUDIES_TRIAL_OBJECTIVES_PURPOSE_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/trial-objectives-and-purpose`,
  STUDIES_TRIAL_DESIGN: '/sponsor/studies/trial-design',
  STUDIES_TRIAL_DESIGN_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/trial-design`,
  STUDIES_TRIAL_TREATMENT_DOSAGE: (interventionId) =>
    `/sponsor/studies/${interventionId}/trial-treatment-and-dosage-regimen-investigational-product`,
  STUDIES_TRIAL_TREATMENT_DOSAGE_BY_ID: (studyId, interventionId) =>
    `/sponsor/studies/${studyId}/trial-treatment-and-dosage-regimen-investigational-product/${interventionId}`,
  STUDIES_PRIOR_CONCOMITANT:
    '/sponsor/studies/prior-and-concomitant-medication',
  STUDIES_PRIOR_CONCOMITANT_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/prior-and-concomitant-medication`,
  STUDIES_COMPARATOR_DESCRIPTION: '/sponsor/studies/comparator-description',
  STUDIES_COMPARATOR_DESCRIPTION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/comparator-description-list`,
  STUDIES_SUBJECT_PARTICIPATION:
    '/sponsor/studies/subject-participation-or-epoch',
  STUDIES_SUBJECT_PARTICIPATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/subject-participation-or-epoch`,
  STUDIES_ASSESSMENT: '/sponsor/studies/efficacy-assessment',
  STUDIES_ASSESSMENT_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/efficacy-assessment`,
  STUDIES_ASSESSMENT_SAFETY: '/sponsor/studies/study-assessments-of-safety',
  STUDIES_ASSESSMENT_SAFETY_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/study-assessments-of-safety`,
  STUDIES_DRUG_CONCENTRATION:
    '/sponsor/studies/drug-concentration-measurements',
  STUDIES_DRUG_CONCENTRATION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/drug-concentration-measurements`,
  STUDIES_DOCUMENTS: '/sponsor/studies/documents',
  STUDIES_DOCUMENTS_BY_ID: (studyId) => `/sponsor/studies/${studyId}/documents`,
  STUDIES_SUBJECT_SELECTION:
    '/sponsor/studies/subject-selection-and-withdrawal',
  STUDIES_SUBJECT_SELECTION_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/subject-selection-and-withdrawal`,
  STUDIES_SUBJECT_WITHDRAWAL: '/sponsor/studies/subject-withdrawal',
  STUDIES_SUBJECT_WITHDRAWAL_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/subject-withdrawal`,
  STUDIES_SIDEBAR_BY_ID: (studyId) =>
    `/sponsor/studies/${studyId}/sidebar-information`,
  //PERSONNEL
  ALL_PERSONNEL: '/personnel',
  PERSONNEL: '/personnels',
  PERSONNEL_ACCOUNT_RESEARCH_SITE: '/personnels/account/research-sites',
  PERSONNEL_ACCOUNT_SPONSOR: '/personnels/account/sponsor',
  PERSONNEL_FILTER_DATA: '/personnels/all/filter-data',
  PERSONNEL_CURRENT_RESEARCH_SITE_BY_ID: (id) =>
    `/personnels/current-research-site/${id}`,
  PERSONNEL_CURRENT_SPONSOR: '/personnels/current-sponsor',
  PERSONNEL_OWN_ACCOUNT: '/personnels/own-account',
  // PDF
  DOWNLOAD_PDF: (query) =>
    `${BASE_PDF_URL}/api/profilepdfgeneration?code=${PDF_GENERATOR_ACCESS_CODE}&${query}`,
  // Personnel
  PERSONNEL_REG: '/registration/personnel',
  PERSONNEL_REG_CLINICAL_QUALIFICATION:
    '/registration/personnel/clinical-qualification',
  PERSONNEL_ANY_LINKING_REQUEST: '/registration/personnel/any-linking-request',
  PERSONNEL_REG_LINK_ORGANIZATIONS:
    '/registration/personnel/link-organizations',
  PERSONNEL_SEARCH_SPONSORS: '/registration/personnel/sponsors',
  PERSONNEL_SEARCH_RESEARCH_SITES: '/registration/personnel/research-sites',
  PERSONNEL_INVITE: '/personnels/invite',
  PERSONNEL_RESEARCH_SITE_LIST: (organizationId) =>
    `/personnels/research-sites/${organizationId}?Page=1&PageSize=500`,
  PERSONNEL_SPONSORS_LIST: (organizationId) =>
    `/personnels/sponsors/${organizationId}?Page=1&PageSize=500`,
  REFERENCE_CATEGORY_BY_ID: (category) => `/references/${category}`,
  RESEARCH_SITES_GET_JOB_TITLES: (id) =>
    `/research-sites/location/${id}/job-titles`,
  //  LINK REQUESTS
  GET_LINKING_REQUESTS: '/link-request',
  LINKING_REQUEST_UNLINK: (userId) => `/link-request/unlink/${userId}`,
  LINKING_REQUEST_ACCEPT: (requestId) => `/link-request/accept/${requestId}`,
  LINKING_REQUEST_DECLINE: (requestId) => `/link-request/decline/${requestId}`,
  LINKING_REQUEST_ACCEPT_BY_USER: (userId) =>
    `/link-request/accept/user/${userId}`,
  LINKING_REQUEST_DECLINE_BY_USER: (userId) =>
    `/link-request/decline/user/${userId}`,
  LINKING_REQUEST_LINK_AGAIN: (userId) => `/link-request/again/${userId}`,
  // ICD10-CM
  ICD10CM_GET_CHAPTERS:
    '/applications/autopopulate/information-related-current-medical-conditions',
  ICD10CM_GET_CHAPTERS_WITH_API_VERSION:
    '/applications/autopopulate/information-related-current-medical-conditions?api-version=2',
  ICD10CM_GET_RANGES: (chapterId) =>
    `/applications/${chapterId}/diagnosis-range`,
  ICD10CM_GET_RANGES_WITH_API_VERSION: (chapterId) =>
    `/applications/${chapterId}/diagnosis-range?api-version=2`,
  ICD10CM_GET_CODES: `/applications/diagnosis-code`,
  ICD10CM_GET_DIAGNOSIS_CODES: `/applications/diagnosis-code?api-version=2`,
  ICD10CM_GET_DIAGNOSIS_CODES_BY_RANGE: (codeRangeId) =>
    `/applications/diagnosis-code?codeRangeId=${codeRangeId}&api-version=2`,
  // ICD10-PCS
  ICD10PCS_GET_CODES: '/icd-procedures',
  // LOINC
  LOINC_GET_CODES: '/loinc',
  // Reference
  REFERENCES_GET_BY_KEY: (key) => `/v2/references/${key}`,
  // Patient
  ALL_PATIENTS: '/patients',
  PATIENTS_ON_STUDIES: '/patients/sponsor',
  PATIENTS_ON_STUDY: (studyId) => `/patients/study/${studyId}`,
  PATIENT_CREATE: '/patients/new',
  PATIENT_UPDATE: '/patients',
  PATIENT_ACTIVATE: '/patients/activate',
  PATIENT_DELETE_DRAFT: (patientId) => `/patients/delete-draft/${patientId}`,
  PATIENT_DELETE_BY_ID: (patientId) => `/patients/unlink/${patientId}`,
  PATIENT_DELETE: '/patients/unlink',
  // Patient / Summary
  PATIENT_SUMMARY_GET: (patientId) => `/patients/summary/${patientId}`,
  PATIENT_DASHBOARD: (role) => `/patients/dashboard/${role}`,
  // Patient / Encounters
  PATIENT_ENCOUNTER_GET: '/clinical-encounter',
  PATIENT_ENCOUNTER_ADD: '/clinical-encounter/new',
  PATIENT_ENCOUNTER_UPDATE: '/clinical-encounter',
  PATIENT_ENCOUNTER_DELETE: (encounterId) =>
    `/clinical-encounter/${encounterId}`,
  // Patient / Clinical Events / Procedures
  PATIENT_PROCEDURE_GET: '/procedure-events',
  PATIENT_PROCEDURE_ADD: '/procedure-events/new',
  PATIENT_PROCEDURE_UPDATE: '/procedure-events',
  PATIENT_PROCEDURE_DELETE: (procedureId) => `/procedure-events/${procedureId}`,
  // Patient / Clinical Events / Comordib Conditions
  PATIENT_COMORBID_CONDITION_GET: '/comorbid-conditions',
  PATIENT_COMORBID_CONDITION_ADD: '/comorbid-conditions/new',
  PATIENT_COMORBID_CONDITION_UPDATE: '/comorbid-conditions',
  PATIENT_COMORBID_CONDITION_DELETE: (comorbidConditionId) =>
    `/comorbid-conditions/${comorbidConditionId}`,
  // Patient / Clinical Events / Death Details
  PATIENT_DEATH_DETAILS_GET: '/death-details',
  PATIENT_DEATH_DETAILS_ADD: '/death-details/new',
  PATIENT_DEATH_DETAILS_UPDATE: '/death-details',
  PATIENT_DEATH_DETAILS_DELETE: (deathDetailsId) =>
    `/death-details/${deathDetailsId}`,
  // Patient / Clinical Events / Lactation Status
  PATIENT_LACTATION_STATUS_GET: '/lactation',
  PATIENT_LACTATION_STATUS_ADD: '/lactation/new',
  PATIENT_LACTATION_STATUS_UPDATE: '/lactation',
  PATIENT_LACTATION_STATUS_DELETE: (lactationStatusId) =>
    `/lactation/${lactationStatusId}`,
  // Patient / Clinical Events / Pregnancy Status
  PATIENT_PREGNANCY_STATUS_GET: '/pregnancy',
  PATIENT_PREGNANCY_STATUS_ADD: '/pregnancy/new',
  PATIENT_PREGNANCY_STATUS_UPDATE: '/pregnancy',
  PATIENT_PREGNANCY_STATUS_DELETE: (pregnancyStatusId) =>
    `/pregnancy/${pregnancyStatusId}`,
  // Patient / Clinical Events / Performance
  PATIENT_PERFORMANCE_GET: '/clinical-performance',
  PATIENT_PERFORMANCE_ADD: '/clinical-performance/new',
  PATIENT_PERFORMANCE_UPDATE: '/clinical-performance',
  PATIENT_PERFORMANCE_DELETE: (performanceId) =>
    `/clinical-performance/${performanceId}`,
  // Patient / Clinical Events / Diagnosis
  PATIENT_DIAGNOSIS_GET: '/patient-diagnosis',
  PATIENT_DIAGNOSIS_ADD: '/patient-diagnosis/new',
  PATIENT_DIAGNOSIS_UPDATE: '/patient-diagnosis',
  PATIENT_DIAGNOSIS_DELETE: (diagnosisId) =>
    `/patient-diagnosis/${diagnosisId}`,
  // Patient / Clinical Events / Vital Signs / Height
  PATIENT_VITAL_SIGNS_HEIGHT_GET: '/vital-signs/height',
  PATIENT_VITAL_SIGNS_HEIGHT_UPDATE: '/vital-signs/height',
  // Patient / Clinical Events / Vital Signs / Weight
  PATIENT_VITAL_SIGNS_WEIGHTS_GET: '/vital-signs/weight',
  PATIENT_VITAL_SIGNS_WEIGHT_ADD: '/vital-signs/weight/new',
  PATIENT_VITAL_SIGNS_WEIGHT_UPDATE: '/vital-signs/weight',
  PATIENT_VITAL_SIGNS_WEIGHT_DELETE: (weightId) =>
    `/vital-signs/weight/${weightId}`,
  // Patient / Vital Signs / Blood Pressure
  PATIENT_VITAL_SIGNS_BLOOD_PRESSURES_GET: '/vital-signs/blood-pressure',
  PATIENT_VITAL_SIGNS_BLOOD_PRESSURE_ADD: '/vital-signs/blood-pressure/new',
  PATIENT_VITAL_SIGNS_BLOOD_PRESSURE_UPDATE: '/vital-signs/blood-pressure',
  PATIENT_VITAL_SIGNS_BLOOD_PRESSURE_DELETE: (bloodPressureId) =>
    `/vital-signs/blood-pressure/${bloodPressureId}`,
  // Patient / Vital Signs / Respiratory Rate
  PATIENT_VITAL_SIGNS_RESPIRATORY_RATES_GET: '/vital-signs/respiratory-rates',
  PATIENT_VITAL_SIGNS_RESPIRATORY_RATE_ADD:
    '/vital-signs/respiratory-rates/new',
  PATIENT_VITAL_SIGNS_RESPIRATORY_RATE_UPDATE: '/vital-signs/respiratory-rates',
  PATIENT_VITAL_SIGNS_RESPIRATORY_RATE_DELETE: (respiratoryRateId) =>
    `/vital-signs/respiratory-rates/${respiratoryRateId}`,
  // Patient / Vital Signs / Temperature
  PATIENT_VITAL_SIGNS_TEMPERATURES_GET: '/vital-signs/temperature',
  PATIENT_VITAL_SIGNS_TEMPERATURE_ADD: '/vital-signs/temperature/new',
  PATIENT_VITAL_SIGNS_TEMPERATURE_UPDATE: '/vital-signs/temperature',
  PATIENT_VITAL_SIGNS_TEMPERATURE_DELETE: (temperatureId) =>
    `/vital-signs/temperature/${temperatureId}`,
  // Patient / Physical Exams
  ADD_PHYSICAL_EXAM: '/physical-exams/new',
  GET_PHYSICAL_EXAMS: '/physical-exams',
  UPDATE_PHYSICAL_EXAM: '/physical-exams',
  DELETE_PHYSICAL_EXAM: (examId) => `/physical-exams/${examId}`,
  // Patient / Labs
  PATIENT_LABS_GET: '/lab-tests',
  PATIENT_LABS_CREATE: '/lab-tests/new',
  PATIENT_LABS_UPDATE: '/lab-tests',
  PATIENT_LABS_DELETE: (labId) => `/lab-tests/${labId}`,
  // Patient / Procedure
  GET_PROCEDURES: '/procedure-prescriptions',
  ADD_PROCEDURE: '/procedure-prescriptions/new',
  UPDATE_PROCEDURE: '/procedure-prescriptions',
  DELETE_PROCEDURE: (procedureId) => `/procedure-prescriptions/${procedureId}`,
  // Patient / Comments
  PATIENT_COMMENTS_GET: '/patient-comments',
  PATIENT_COMMENT_ADD: '/patient-comments/new',
  PATIENT_COMMENT_UPDATE: '/patient-comments',
  PATIENT_COMMENT_DELETE: (commentId) => `/patient-comments/${commentId}`,
  // Patient / Medications
  PATIENT_MEDICATION_GET: '/medications',
  PATIENT_MEDICATION_ADD: '/medications/new',
  PATIENT_MEDICATION_UPDATE: '/medications',
  PATIENT_MEDICATION_DELETE: (medicationId) => `/medications/${medicationId}`,
  // Patient
  PATIENT_GET: (patientId) => `/patients/${patientId}`,
  PATIENT_ON_STUDY_GET: (patientId) =>
    `/patients/patient-demography/${patientId}`,
  // Diagnosis (Patient-based)
  DIAGNOSIS_CHAPTERS_BY_PATIENT_ID: (patientId) =>
    `/diagnosis/${patientId}/chapters`,
  DIAGNOSIS_RANGES_BY_PATIENT_ID: (patientId, chapterId) =>
    `/diagnosis/${patientId}/ranges?chapterId=${chapterId}`,
  DIAGNOSIS_CODES_BY_PATIENT_ID: (patientId, rangeId, search) =>
    `/diagnosis/${patientId}/codes?rangeId=${rangeId}&search=${search}`,
  // Patient Matches
  PATIENT_MATCHES_GET: '/patients/matches',

  PATIENT_STUDIES_GET: (
    patientId,
    page,
    pageSize,
    search,
    clinicalTrialStudyStatus,
    clinicalTrialStudyType,
    compassionateUseStudyStatus,
    compassionateUseStudyType,
    studyPhase,
    ageGroup,
    sex,
    acceptsHealthyVolunteers,
    inclusionCriteria,
    exclusionCriteria,
    country,
    city,
    state,
    onlyStudyTitle,
    populationDiseaseCondition,
    intervention,
    sponsorName,
    studyIds,
    studyId,
  ) =>
    `/sponsor/studies/matched-studies-by-patient${getUrlQueryStringPart({
      patientId,
      page,
      pageSize,
      search,
      clinicalTrialStudyStatus,
      clinicalTrialStudyType,
      compassionateUseStudyStatus,
      compassionateUseStudyType,
      studyPhase,
      ageGroup,
      sex,
      acceptsHealthyVolunteers,
      inclusionCriteria,
      exclusionCriteria,
      country,
      city,
      state,
      onlyStudyTitle,
      populationDiseaseCondition,
      intervention,
      sponsorName,
      studyIds,
      studyId,
    })}`,
  PATIENT_APPLICATIONS_GET: (
    patientId,
    page,
    pageSize,
    search,
    orderBy,
    sponsorName,
    contactPersonResponsibleForApplication,
    sponsorContact,
    protocolTitle,
    protocolIdentificationNumber,
    researchSiteName,
    clinicalTrialStatus,
    compassionateUseStatus,
    applicationStatus,
    studyType,
    studyStatus,
  ) =>
    `/applications/patient/${patientId}${getUrlQueryStringPart({
      'api-version': 1,
      Page: page,
      PageSize: pageSize,
      Search: search,
      OrderBy: orderBy,
      SponsorName: sponsorName,
      ContactPersonResponsibleForApplication:
        contactPersonResponsibleForApplication,
      SponsorContact: sponsorContact,
      ProtocolTitle: protocolTitle,
      ProtocolIdentificationNumber: protocolIdentificationNumber,
      ResearchSiteName: researchSiteName,
      ClinicalTrialStatus: clinicalTrialStatus,
      CompassionateUseStatus: compassionateUseStatus,
      ApplicationStatus: applicationStatus,
      StudyType: studyType,
      StudyStatus: studyStatus,
    })}`,

  PATIENT_APPLICATIONS_FILTER_DATA_GET: (patientId) =>
    patientId
      ? `/applications/patient/${patientId}/filter-data${getUrlQueryStringPart({
          'api-version': 1,
        })}`
      : `/applications/filter-data${getUrlQueryStringPart({
          'api-version': 1,
        })}`,
};

export const LOCAL_STORAGE_KEYS = {
  USER: 'USER',
  LOGOUT_TIMER: 'LOGOUT_TIMER',
  LOGOUT_DATE: 'LOGOUT_DATE',
  STUDIES_COUNT: 'STUDIES_COUNT',
  CLICK_SOURCE: 'CLICK_SOURCE',
  SIDEBAR_IS_OPEN: 'SIDEBAR_IS_OPEN',
  EDIT_PAGE: 'EDIT_PAGE',
  PERSONNEL_ACTION_FLAG: 'PERSONNEL_ACTION_FLAG',
  SPONSOR_ADDITIONAL_PERSONNEL: 'SPONSOR_ADDITIONAL_PERSONNEL',
  BREADCRUMBS_ARRAY: 'BREADCRUMBS_ARRAY',
  STUDY_TYPE: 'STUDY_TYPE',
  STUDY_TYPE_FOR_TITLE: 'STUDY_TYPE_FOR_TITLE',
};
export const UPLOAD_URLS = {
  files: UPLOAD_FILES_URL,
  images: UPLOAD_IMAGES_URL,
};

export const ROLES = {
  ADMINISTRATOR: 'Administrator',
  REPRESENTATIVE: 'Representative',
  SPONSOR: 'Sponsor',
  RESEARCH_SITE: 'ResearchSite',
  RESEARCH_SITE_REGISTRATION: 'ResearchSiteRegistration',
  SPONSOR_REGISTRATION: 'SponsorRegistration',
  PERSONNEL_REGISTRATION: 'PersonnelRegistration',
  PERSONNEL_INACTIVE: 'InactivePersonnel',
  BLOCKED: 'Blocked',
  PERSONNEL: 'Personnel',
  RESEARCH_SITE_PERSONNEL: 'ResearchSitePersonnel',
  SPONSOR_PERSONNEL: 'SponsorPersonnel',
  INACTIVE_SPONSOR: 'InactiveSponsor',
  INACTIVE_RESEARCH_SITE: 'InactiveResearchSite',
};

export const PERSONNEL_TYPES = {
  // Backend enum is `PersonnelType.ResearchSitesPersonnel`
  RESEARCH_SITE: 'ResearchSitesPersonnel',
  SPONSOR: 'SponsorPersonnel',
};

export const FUNCTIONAL_ROLES = {
  SPONSOR_ADMIN: 'SPONSOR_ADMIN',
  SPONSOR_PERSONNEL: 'SPONSOR_PERSONNEL',
  RESEARCH_SITE_ADMIN: 'RESEARCH_SITE_ADMIN',
  RESEARCH_SITE_PERSONNEL: 'RESEARCH_SITE_PERSONNEL',
  PERSONNEL_INDIVIDUAL: 'PERSONNEL_INDIVIDUAL',
};

export const GROUPS = {
  INACTIVE_SPONSOR_GROUP: 'InactiveSponsor',
  INACTIVE_RESEARCH_SITE_GROUP: 'InactiveResearchSite',
  SPONSOR_GROUP: 'Sponsor',
};

export const REQUEST_DIRECTION = {
  INCOMING: 1,
  OUTCOMING: 2,
};

export const REQUEST_TYPES = {
  REQUEST: 'Request',
  INVITE: 'Invite',
};

export const REQUEST_STATUSES = {
  NEW_REQUEST_ACCESS_STATUS_KEY: 1,
  ACCESS_GRANTED_ACCESS_STATUS_KEY: 2,
  ACCESS_DENIED_ACCESS_STATUS_KEY: 3,
  NOT_RESPOND_ACCESS_STATUS_KEY: 4,
  DISCONNECTED_ACCESS_STATUS_KEY: 5,
  OVERRIDDEN_ACCESS_STATUS_KEY: 6,
};

export const REQUEST_STATUSES_LINKING_REQUEST = {
  NEW_REQUEST: 1,
  ACCEPTED: 2,
  DECLINED: 3,
  EXPIRED: 4,
  DISCONNECTED: 5,
};

export const REQUEST_AVAILABLE_ACTIONS = {
  ACCEPT: 'Accept',
  DECLINE: 'Decline',
  CONNECT: 'Connect',
  DISCONNECT: 'Disconnect',
  VIEW_DETAILS: 'ViewDetails',
  DOWNLOAD_PDF: 'DownloadPdf',
};

export const PDF_PATHNAMES = {
  SPONSOR: 'ViewSponsorDetails',
  RESEARCH_SITE: 'ViewResearchSiteDetails',
  PERSONNEL: 'ViewPersonnelDetails',
};

export const COLLABORATOR_CLASSES = {
  1: 'U.S. National Institutes of Health',
  2: 'Other U.S. Federal agencies',
  3: 'Industry',
  4: 'Other',
};

export const STUDY_TYPE = {
  1: 'Clinical trial',
  2: 'Compassionate use',
};

export const STUDY_STATUS = {
  1: 'Not yet recruiting',
  2: 'Recruiting',
  3: 'Enrolling by invitation',
  4: 'Active, not recruiting',
  5: 'Suspended',
  6: 'Unknown status',
  7: 'All',
  8: 'Not chosen',
};

export const STUDY_STATUS_CLINICAL_TRIAL = {
  1: 'Not yet recruiting',
  2: 'Recruiting',
  3: 'Enrolling by invitation',
  4: 'Active, not recruiting',
  5: 'Suspended',
  6: 'Unknown status',
};

export const STUDY_STATUS_COMPASSIONATE_USE = {
  1: 'Available',
  2: 'Temporarily not available',
};

export const GENDER_TYPE = {
  1: 'Male',
  2: 'Female',
  3: 'Unknown',
  4: 'Other',
};

export const CLINICAL_STATUS = {
  1: 'Active',
  2: 'Recurrence',
  3: 'Relapse',
  4: 'Inactive',
  5: 'Remission',
  6: 'Resolved',
};

export const ALLOWED_STATUSES = {
  REPRESENTATIVE_STATUS: 'Representative',
  PERSONNEL_STATUS: 'Personnel',
  RESEARCH_SITE_STATUS: 'ResearchSite',
  SPONSOR_STATUS: 'Sponsor',
  GRANTED_VERIFICATION_STATUS: 'GrantedVerification',
  DENIED_VERIFICATION_STATUS: 'DeniedVerification',
  PENDING_VERIFICATION_STATUS: 'PendingVerification',
};

export const MAP_PARAMS = {
  DEFAULT_BOUNDS: {
    maxY: 49.699121409215586,
    maxX: -71.06921402721613,
    minY: 28.22339331112454,
    minX: -134.35046402721613,
  },
  DEFAULT_ZOOM: 5,
  DEFAULT_CENTER: { lat: 39.85941224560119, lng: -102.75378433971612 },
};

export const STUDY_PAGE_ABSENT_SPONSOR_PLACEHOLDER = 'sponsorId';

export function getDmsDocumentAccessPath(personnelUserId) {
  return `${ROUTES.DMS_DOCUMENTS}/personnel/${personnelUserId}/access`;
}
